import React, { FC, Fragment, useEffect, useState } from 'react';
import { Button, Checkbox, Form } from 'antd';
import { useIntl } from 'react-intl';
import {
  CustomInputText,
  CustomInputTextArea,
  CustomOnlyNumber,
} from 'src/components/forms/pin/CustomInputText';
import { ComboData, IAddressData } from 'src/shared/models';
import {
  customRegexName,
  phoneRegex,
  useWindowSize,
  zipCodeFormatPortugal,
  nifFormat,
  directionCharacters,
} from 'utils';
import { IAddressFormValues } from 'src/components/forms/interfaces';

interface Props {
  address?: IAddressData;
  isEditForm?: boolean;
  isFirstAddress?: boolean;
  onCreateAddress?: (address: IAddressData) => void;
  onUpdateAddress?: (address: IAddressData) => void;
  regionList: ComboData[];
  isLoading?: boolean;
}

const AddressFormPt: FC<Props> = ({
  address,
  isEditForm,
  isFirstAddress,
  onCreateAddress,
  onUpdateAddress,
  regionList,
  isLoading = false,
}): JSX.Element => {
  const [form] = Form.useForm();
  const { isMD } = useWindowSize();
  const { formatMessage } = useIntl();
  const [addressData, setAddressData] = useState(address);

  const formValues: Partial<IAddressData> = {
    name: address?.name || '',
    surname: address?.surname || '',
    address: address?.address || '',
    phone: address?.phone || '',
    zipCode: address?.zipCode || '',
    city: address?.city || '',
    regionName: address?.regionName || '',
    deliveryInstructions: address?.deliveryInstructions || '',
    idNumber: address?.idNumber || '',
  };

  let formFields: IAddressFormValues[] = [
    {
      type: 'text',
      name: 'name',
      placeholder: 'form.field.name',
      handleChange: 'name',
      initialValue: formValues.name,
      rules: {
        pattern: customRegexName,
        message: formatMessage(
          { id: 'form.field.error-only-letters' },
          { name: formatMessage({ id: 'form.field.name' }) },
        ),
      },
      autocomplete: 'given-name',
      length: 25,
      value: addressData?.name,
    },
    {
      type: 'text',
      name: 'surname',
      placeholder: 'form.field.last-name',
      handleChange: 'surname',
      initialValue: formValues.surname,
      rules: {
        pattern: customRegexName,
        message: formatMessage(
          { id: 'form.field.error-only-letters' },
          { name: formatMessage({ id: 'form.field.last-name' }) },
        ),
      },
      autocomplete: 'family-name',
      length: 50,
      value: addressData?.surname,
    },
    {
      type: 'text',
      name: 'address',
      placeholder: 'form.field.address',
      handleChange: 'address',
      initialValue: formValues.address,
      rules: {
        pattern: directionCharacters,
        message: formatMessage({ id: 'form.field.error-restricted-error' }),
      },
      autocomplete: 'street-address',
      length: 35,
      value: addressData?.address,
    },
    {
      type: 'number',
      name: 'zipCode',
      placeholder: 'form.field.zipCode',
      handleChange: 'zipCode',
      initialValue: formValues.zipCode,
      rules: {
        validator: async (_, zipCode) => {
          if (
            !zipCode.match(zipCodeFormatPortugal) ||
            !getRegionByZip(zipCode)
          ) {
            throw new Error(
              formatMessage(
                { id: 'form.field.zipCode-error' },
                { name: formatMessage({ id: 'form.field.zip-code' }) },
              ),
            );
          }
        },
      },
      autocomplete: 'postal-code',
      length: 7,
      value: addressData?.zipCode,
    },
    {
      type: 'text',
      name: 'city',
      placeholder: 'form.field.city',
      handleChange: 'city',
      initialValue: formValues.city,
      rules: {
        pattern: customRegexName,
        message: formatMessage(
          { id: 'form.field.error-only-letters' },
          { name: formatMessage({ id: 'form.field.city' }) },
        ),
      },
      autocomplete: 'address-level2',
      length: 50,
      value: addressData?.city,
    },
    {
      type: 'text',
      name: 'idNumber',
      placeholder: 'form.field.idNumber',
      handleChange: 'idNumber',
      initialValue: formValues?.idNumber,
      rules: {
        pattern: nifFormat,
        message: formatMessage(
          { id: 'form.field.idNumber-error' },
          { name: formatMessage({ id: 'form.field.idNumber' }) },
        ),
      },
      length: 9,
      value: addressData?.idNumber,
    },
    {
      type: 'number',
      name: 'phone',
      placeholder: 'form.field.telephone',
      handleChange: 'phone',
      initialValue: formValues?.phone,
      rules: {
        pattern: phoneRegex.PT,
        message: formatMessage({
          id: 'form.field.phone-error-number',
        }),
      },
      autocomplete: 'tel',
      length: 9,
      value: addressData?.phone,
    },
    {
      type: 'optionalTextArea',
      name: 'deliveryInstructions',
      placeholder: 'form.field.deliveryInstructions',
      handleChange: 'deliveryInstructions',
      initialValue: '',
      rules: {
        pattern: directionCharacters,
        message: formatMessage({ id: 'form.field.error-restricted-error' }),
      },
      length: 250,
      value: addressData?.deliveryInstructions,
    },
  ];

  useEffect(() => {
    checkCanContinue();
  });

  useEffect(() => {
    form.resetFields();
    if (!addressData) return;
    form.setFieldsValue(formValues);
  }, []);

  useEffect(() => {
    if (!isFirstAddress) return;

    setAddressData((currentAddress) => {
      return {
        ...currentAddress,
        defaultAddress: true,
      };
    });
  }, []);

  const zipCode = addressData?.zipCode;

  useEffect(() => {
    setRegionNumberByZip(zipCode);
  }, [zipCode]);

  const getRegionByZip = (zipCode) => {
    const zipCodePrefix = zipCode.slice(0, 4);
    return regionList.find((region) => region.value === zipCodePrefix);
  };

  const setRegionNumberByZip = (zipCode) => {
    const isZipCodeValid = zipCode?.length === 7;
    const region = isZipCodeValid ? getRegionByZip(zipCode) : undefined;
    setAddressData((data) => {
      return {
        ...data,
        regionName: region?.description ?? '',
      };
    });
  };

  const isDefaultAddressChecked = (): boolean => {
    if ((!isEditForm && isFirstAddress) || address?.defaultAddress) {
      return true;
    }
    return false;
  };

  const isDefaultAddressDisabled = () => {
    const editingDefaultAddress = Boolean(isEditForm && address.defaultAddress);

    if (isFirstAddress || editingDefaultAddress) return true;
    return false;
  };

  const handleSubmit = () => {
    isEditForm ? onUpdateAddress(addressData) : onCreateAddress(addressData);
    form.resetFields();
  };

  const checkAddressForm = (addressData: IAddressData): boolean => {
    return Boolean(
      addressData?.name?.match(customRegexName) &&
        addressData?.surname?.match(customRegexName) &&
        addressData?.address &&
        addressData?.phone?.match(phoneRegex.PT) &&
        addressData?.zipCode?.match(zipCodeFormatPortugal) &&
        addressData?.city?.match(customRegexName) &&
        addressData?.regionName &&
        addressData?.idNumber,
    );
  };

  const checkCanContinue = () => {
    const isDataCorrect = checkAddressForm(addressData);
    if (addressData?.canContinue == isDataCorrect) return;
    setAddressData((data) => {
      return {
        ...data,
        canContinue: isDataCorrect,
      };
    });
  };

  const handleChange = (field: string) => (event: any) => {
    if (event.persist) event.persist();
    setAddressData((data) => {
      const newData = { ...data };
      let value = event?.target?.value;

      switch (field) {
        case 'phone':
        case 'zipCode':
          value = event;
          break;
        case 'defaultAddress':
          value = event.target.checked;
          break;
        default:
          value = event.target.value;
          break;
      }

      return { ...newData, [field]: value };
    });
  };

  const renderTitle = (
    <>
      {isMD && (
        <h4 className="address__form-title">
          {isEditForm
            ? formatMessage({ id: 'form.address.edit-title' })
            : formatMessage({ id: 'form.address.new-title' })}
        </h4>
      )}
      {!isMD && !isEditForm && (
        <h4 className="address__form-title">
          {formatMessage({ id: 'form.address.new-title' })}
        </h4>
      )}
    </>
  );

  const renderFields = formFields.map((field) => {
    let component: JSX.Element;
    switch (field.type) {
      case 'text':
        component = CustomInputText(
          form,
          field.name,
          field.placeholder,
          handleChange(field.name),
          field.initialValue,
          field.length,
          true,
          [field.rules],
          field.value || null,
          field.autocomplete,
        );
        break;
      case 'optionalText':
        component = CustomInputText(
          form,
          field.name,
          field.placeholder,
          handleChange(field.name),
          field.initialValue,
          field.length,
          false,
          [field.rules],
          field.value || null,
          field.autocomplete,
        );
        break;
      case 'optionalTextArea':
        component = CustomInputTextArea(
          form,
          field.name,
          field.placeholder,
          handleChange(field.name),
          field.initialValue,
          field.length ?? 250,
          false,
          [field.rules],
          field.value || null,
          field.autocomplete,
        );
        break;
      case 'number':
        component = CustomOnlyNumber(
          form,
          field.name,
          field.placeholder,
          handleChange(field.name),
          field.initialValue,
          field.length ?? 40,
          true,
          [field.rules],
          field.value || '',
          field.autocomplete,
        );
        break;
      default:
        return;
    }

    return <Fragment key={field.name}>{component}</Fragment>;
  });

  return (
    <>
      {renderTitle}
      <Form
        name="addressForm"
        form={form}
        className="address__form"
        onFinish={handleSubmit}>
        {renderFields}
        {!isDefaultAddressDisabled() && (
          <Form.Item
            name="defaultAddress"
            valuePropName="checked"
            initialValue={isDefaultAddressChecked()}>
            <Checkbox
              className="address__form__button-wrapper"
              defaultChecked={isDefaultAddressChecked()}
              onChange={handleChange('defaultAddress')}
              disabled={isDefaultAddressDisabled()}>
              {formatMessage({ id: 'form.field.address.preferred' })}
            </Checkbox>
          </Form.Item>
        )}
        <Form.Item shouldUpdate className="address__form__button-wrapper">
          {() => (
            <Button
              htmlType="submit"
              className="address__form__button-submit btn-primary btn-primary--black"
              disabled={!addressData?.canContinue || isLoading}>
              {formatMessage({ id: 'form.address.add-new' })}
              {isMD && (
                <i className="icon icon--arrow-normal-right-white button-icon" />
              )}
            </Button>
          )}
        </Form.Item>
      </Form>
    </>
  );
};

export default AddressFormPt;
